import { ApolloQueryResult } from '@apollo/client'
import { Button, Heading, HFlow, Icon, Text, VFlow } from 'bold-ui'
import { Breadcrumb } from 'components/breadcrumb'
import { PageContent } from 'components/layout/PageContent'
import { ATIVIDADE_COLETIVA_GESTAO_GRUPOS_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { TitleGroup } from 'components/TitleGroup'
import { css } from 'emotion'
import { AtividadeColetivaGruposQuery, TipoGrupoParticipanteAtividadeColetivaEnum } from 'graphql/types.generated'
import { Fragment } from 'react'
import { useHistory } from 'react-router'
import { formatDate } from 'util/date/formatDate'
import { AtividadeColetivaGruposVisualizarDropdownMenu } from 'view/atividade-coletiva/componentes/AtividadeColetivaGruposVisualizarDropdownMenu'
import { LotacoesEnvolvidasTable } from 'view/atividade-coletiva/componentes/LotacoesEnvolvidasTable'

import { AtividadeColetivaGruposInfoHeader } from '../AtividadeColetivaGruposInfoHeader'
import { CidadaoTable } from '../CidadaoTable'
import { useAtividadeColetivaGruposExcluir } from '../hooks/useAtividadeColetivaGruposExcluir'
import { useAtividadeColetivaGruposStatusAtivoUpdate } from '../hooks/useAtividadeColetivaGruposStatusAtivoUpdate'
import { AtividadeColetivaGruposModel } from '../model-atividadeColetivaGrupos'

interface AtividadeColetivaGruposVisualizarViewProps {
  atividadeColetivaGrupo: AtividadeColetivaGruposModel
  refetch: () => Promise<ApolloQueryResult<AtividadeColetivaGruposQuery>>
}

export const AtividadeColetivaGruposVisualizarView = (props: AtividadeColetivaGruposVisualizarViewProps) => {
  const { atividadeColetivaGrupo, refetch } = props

  const mustShowParticipantes =
    atividadeColetivaGrupo.tipoGrupo === TipoGrupoParticipanteAtividadeColetivaEnum.ATIVIDADE_EM_SAUDE
  const history = useHistory()

  const isGrupoAtivo = atividadeColetivaGrupo.ativo

  const { profissional, cbo } = atividadeColetivaGrupo.lotacaoAtualizacaoStatusAtivo
  const nomeProfissional = profissional.nome ?? profissional.nomeSocial

  const statusAtivoInfo = `Grupo ${isGrupoAtivo ? 'ativado' : 'inativado'} em ${formatDate(
    atividadeColetivaGrupo.dataAtualizacaoStatusAtivo
  )} por ${nomeProfissional} - ${cbo.nome}`

  const { excluirGrupo } = useAtividadeColetivaGruposExcluir({
    onSuccess: () => {
      window.history.back()
    },
  })

  const handleEditar = (grupoParticipanteAtividadeColetivaId: ID) => {
    history.push(`${ATIVIDADE_COLETIVA_GESTAO_GRUPOS_PATH}/editar/${grupoParticipanteAtividadeColetivaId}`, {
      fromView: true,
    })
  }

  const { updateStatusAtivo } = useAtividadeColetivaGruposStatusAtivoUpdate({
    onSuccess: () => {
      refetch()
    },
  })

  return (
    <Fragment>
      <Breadcrumb title='Visualização de grupo' />
      <PageContent type='filled' style={styles.pageContent}>
        <VFlow vSpacing={2}>
          <HFlow justifyContent='space-between'>
            <TitleGroup title='Grupo de atividade coletiva' />
            <HFlow hSpacing={0.5} justifyContent='flex-end'>
              <Button kind='primary' size='small' onClick={() => handleEditar(atividadeColetivaGrupo.id)}>
                <Icon icon='penOutline' />
                Editar
              </Button>
              <AtividadeColetivaGruposVisualizarDropdownMenu
                onExcluir={() => excluirGrupo(atividadeColetivaGrupo.id)}
              />
            </HFlow>
          </HFlow>
          <AtividadeColetivaGruposInfoHeader atividadeColetivaGrupo={atividadeColetivaGrupo} />
          <VFlow vSpacing={1.5}>
            <Heading level={2}>Profissionais envolvidos</Heading>
            <LotacoesEnvolvidasTable items={atividadeColetivaGrupo.lotacoesEnvolvidas} />
          </VFlow>
          {mustShowParticipantes && (
            <VFlow vSpacing={1.5}>
              <Heading level={2}>Listagem de participantes</Heading>
              <CidadaoTable items={atividadeColetivaGrupo.cidadaosParticipantes} />
            </VFlow>
          )}
          <VFlow vSpacing={1}>
            <Button
              size='small'
              kind='normal'
              onClick={() => updateStatusAtivo(atividadeColetivaGrupo.id, isGrupoAtivo)}
            >
              {isGrupoAtivo ? 'Inativar grupo' : 'Ativar grupo'}
            </Button>
            <Text>{statusAtivoInfo}</Text>
          </VFlow>
        </VFlow>
      </PageContent>
    </Fragment>
  )
}

const styles = {
  pageContent: css`
    padding-bottom: 3.5rem;
  `,
}
